import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'src/services';
import { Comment, CommentEntityType } from 'src/models';
import { NonOptional } from '@insight-health-ai/roseate/utils';
import {
  UseInfiniteQueryOverrideOptions,
  UseQueryOverrideOptions,
} from '../common';

export type GetCommentsResponse = {
  comments: Comment[];
  page: number;
  per_page: number;
  total: number;
};

export type UseGetCommentListParams = {
  entityType: CommentEntityType;
  entityId: string;
  page?: number;
  perPage?: number;
};

const filterToParams = ({
  entityType,
  entityId,
  page,
  perPage,
}: UseGetCommentListParams) => ({
  entity_type: entityType,
  entity_id: entityId,
  page,
  per_page: perPage,
});

const defaultPerPage = 50;

export const useGetCommentList = (
  filters: UseGetCommentListParams,
  options: UseQueryOverrideOptions<GetCommentsResponse> = {}
) => {
  const { entityType, entityId } = filters;
  const transformedFilters = filterToParams({
    perPage: defaultPerPage,
    page: 1,
    ...filters,
  });

  return useQuery({
    ...options,
    queryKey: ['commentList', entityType, entityId],
    queryFn: () =>
      api
        .get<GetCommentsResponse>(`/v3/comments`, {
          params: transformedFilters,
        })
        .then(({ data }) => data),
    enabled: options.enabled ?? Boolean(entityId),
  });
};

export const useGetCommentInfiniteList = (
  filters: UseGetCommentListParams,
  options: UseInfiniteQueryOverrideOptions = {}
) => {
  const { entityType, entityId } = filters;

  return useInfiniteQuery({
    ...options,
    queryKey: ['commentList', 'infinite', entityType, entityId],
    queryFn: ({ pageParam }) =>
      api
        .get<GetCommentsResponse>(`/v3/comments`, {
          params: filterToParams(pageParam),
        })
        .then(({ data }) => data),
    initialPageParam: {
      perPage: defaultPerPage,
      page: 1,
      ...filters,
    } as NonOptional<UseGetCommentListParams, 'page' | 'perPage'>,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.per_page < lastPage.total
        ? { perPage: defaultPerPage, ...filters, page: lastPage.page + 1 }
        : undefined,
  });
};

export const useCommentListClient = () => {
  const queryClient = useQueryClient();

  const scanComment = (
    params: UseGetCommentListParams & { commentId: Comment['id'] },
    replacer: (comment: Comment) => Comment
  ) => {
    const { entityType, entityId, commentId } = params;
    queryClient.setQueriesData<ReturnType<typeof useGetCommentList>['data']>(
      { queryKey: ['commentList', entityType, entityId] },
      (oldData) => {
        if (!oldData?.comments?.length) return oldData;

        return {
          ...oldData,
          comments: oldData.comments.map((c) =>
            c.id === commentId ? replacer(c) : c
          ),
        };
      }
    );

    queryClient.setQueriesData<
      ReturnType<typeof useGetCommentInfiniteList>['data']
    >(
      {
        queryKey: ['commentList', 'infinite', entityType, entityId],
      },
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            comments: page.comments.map((c) =>
              c.id === commentId ? replacer(c) : c
            ),
          })),
        };
      }
    );
  };

  const addComment = (
    params: UseGetCommentListParams & { comment: Comment }
  ) => {
    const { entityType, entityId, comment } = params;
    queryClient.setQueriesData<ReturnType<typeof useGetCommentList>['data']>(
      { queryKey: ['commentList', entityType, entityId] },
      (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, comments: [...oldData.comments, comment] };
      }
    );

    queryClient.setQueriesData<
      ReturnType<typeof useGetCommentInfiniteList>['data']
    >(
      { queryKey: ['commentList', 'infinite', entityType, entityId] },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: [
            ...oldData.pages,
            {
              comments: [comment],
              page: oldData.pages[oldData.pages.length - 1].page + 1,
              per_page: oldData.pages[oldData.pages.length - 1].per_page,
              total: oldData.pages[oldData.pages.length - 1].total + 1,
            },
          ],
        };
      }
    );
  };

  const invalidate = (params: UseGetCommentListParams) => {
    const { entityType, entityId } = params;
    queryClient.invalidateQueries({
      queryKey: ['commentList', entityType, entityId],
    });
    queryClient.invalidateQueries({
      queryKey: ['commentList', 'infinite', entityType, entityId],
    });
  };

  const deleteComment = (
    params: UseGetCommentListParams & { commentId: Comment['id'] }
  ) => {
    const { entityType, entityId, commentId } = params;
    queryClient.setQueriesData<ReturnType<typeof useGetCommentList>['data']>(
      { queryKey: ['commentList', entityType, entityId] },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          comments: oldData.comments.filter((c) => c.id !== commentId),
        };
      }
    );
    queryClient.setQueriesData<
      ReturnType<typeof useGetCommentInfiniteList>['data']
    >(
      { queryKey: ['commentList', 'infinite', entityType, entityId] },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            comments: page.comments.filter((c) => c.id !== commentId),
          })),
        };
      }
    );
  };

  return { scanComment, invalidate, addComment, deleteComment };
};
